import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Button, Card, Heading, HStack, Separator, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { BaseballCard } from '@src/components/sco/Advisors/cards/BaseballCard';
import { ADVISORS_MY_FOUNDERS_GRID_SIZE, ADVISORS_ORDER_BY_LAST_ACTIVITY } from '@src/components/sco/Advisors/constants';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { engagementToFounderBaseballCard, founderDisplayName, founderUserIdOf, isActiveFounderEngagement } from '@src/components/sco/Advisors/utils/mapFounderEngagements';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE } from '@src/constants/advisor-engagement-statuses';
import { ADVISOR_FOUNDER_ENGAGEMENTS } from '@src/gql/queries/AdvisorFounderEngagements';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useMemo } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
type FounderEngagementsResult = {
  advisorEngagements?: {
    items?: Array<ScoAdvisorEngagement | null> | null;
    pagination?: {
      itemCount?: number | null;
      pageCount?: number | null;
      hasNextPage?: boolean | null;
    } | null;
  } | null;
};
export const MyFoundersSection = () => {
  const $ = _c(40);
  const paths = useAdvisorsPaths();
  useCurrentUser();
  const isMobile = useIsMobile();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      variables: {
        statusKeys: [ADVISOR_ENGAGEMENT_STATUS_ACTIVE],
        page: 1,
        pageSize: ADVISORS_MY_FOUNDERS_GRID_SIZE,
        orderBy: ADVISORS_ORDER_BY_LAST_ACTIVITY
      },
      fetchPolicy: "cache-and-network"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const gridQuery = useQuery(ADVISOR_FOUNDER_ENGAGEMENTS, t0);
  gridQuery.data?.advisorEngagements?.items;
  let t1;
  if ($[1] !== gridQuery.data?.advisorEngagements?.items) {
    t1 = gridQuery.data?.advisorEngagements?.items || [];
    $[1] = gridQuery.data?.advisorEngagements?.items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = t1.filter(isActiveFounderEngagement);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const gridEngagements = t2 as ScoAdvisorEngagement[];
  let t3;
  if ($[5] !== gridEngagements || $[6] !== paths.base) {
    let t4;
    if ($[8] !== paths.base) {
      t4 = engagement => ({
        key: engagement.advisorEngagementId || engagement.id || founderUserIdOf(engagement) || founderDisplayName(engagement),
        card: engagementToFounderBaseballCard(engagement, {
          base: paths.base
        })
      });
      $[8] = paths.base;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = gridEngagements.map(t4);
    $[5] = gridEngagements;
    $[6] = paths.base;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const cards = t3;
  const itemCount = gridQuery.data?.advisorEngagements?.pagination?.itemCount ?? gridEngagements.length;
  const hasMoreFounders = itemCount > cards.length || !!gridQuery.data?.advisorEngagements?.pagination?.hasNextPage;
  let t4;
  if ($[10] !== paths.base || $[11] !== paths.myFounders) {
    t4 = advisorsBreadcrumbsWithTrail(paths.base, {
      title: "My founders",
      href: paths.myFounders
    });
    $[10] = paths.base;
    $[11] = paths.myFounders;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const toolbarBreadcrumbs = t4;
  let t5;
  if ($[13] !== toolbarBreadcrumbs) {
    t5 = {
      breadcrumbs: toolbarBreadcrumbs
    };
    $[13] = toolbarBreadcrumbs;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useAdvisorsToolbar(t5);
  const loading = gridQuery.loading && !gridQuery.data;
  const error = gridQuery.error;
  const t6 = isMobile ? "xl" : "3xl";
  let t7;
  if ($[15] !== t6) {
    t7 = <Heading size={t6}>My Founders</Heading>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = isMobile ? "sm" : "md";
  let t9;
  if ($[17] !== t8) {
    t9 = <Text color="colorPalette.fg/60" fontSize={t8} maxWidth="2xl">Founders you advise — open a conversation or review their profile.</Text>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t7 || $[20] !== t9) {
    t10 = <VStack align="stretch" gap={1}>{t7}{t9}</VStack>;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Separator />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text color="colorPalette.fg/60" textStyle="labelXs">Active relationships</Text>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = itemCount === 1 ? "founder" : "founders";
  let t14;
  if ($[24] !== itemCount || $[25] !== t13) {
    t14 = <HStack align="center" justify="space-between">{t12}<Badge colorPalette="orange" variant="subtle">{itemCount} {t13}</Badge></HStack>;
    $[24] = itemCount;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== cards || $[28] !== hasMoreFounders || $[29] !== paths.base || $[30] !== paths.myFoundersAll) {
    t15 = cards.length === 0 ? <VStack align="center" borderColor="colorPalette.border" borderRadius="xl" borderStyle="dashed" borderWidth="1px" gap={3} justify="center" padding={4} paddingY={10}><Text color="colorPalette.fg/60" textAlign="center">No active founder relationships yet. Accept board invites or apply to open seats from Opportunities.</Text><Button asChild={true} size="sm" variant="cta"><NextLink href={`${paths.base}/opportunities`}>Go to Opportunities</NextLink></Button></VStack> : <SimpleGrid columns={{
      base: 1,
      md: 2,
      xl: 3
    }} gap={5}>{cards.map(_temp)}{hasMoreFounders ? <Card.Root variant="outline"><Card.Body><HStack align="center" gap={4} justify="space-between" wrap="wrap"><VStack align="stretch" gap={1}><Heading size="md">Looking for someone?</Heading><Text color="colorPalette.fg/70" fontSize="sm">Browse your full founder roster, or explore new board opportunities.</Text></VStack><HStack gap={2} wrap="wrap"><Button asChild={true} variant="outline"><a href={paths.myFoundersAll}>View all</a></Button><Button asChild={true} variant="cta"><a href={`${paths.base}/opportunities`}>Opportunities</a></Button></HStack></HStack></Card.Body></Card.Root> : <Card.Root asChild={true} variant="outline"><a href={`${paths.base}/opportunities`}><Card.Body><VStack align="center" gap={4} height="full" justify="center"><VStack align="stretch" gap={1} textAlign="center"><Heading size="md">Find more founders</Heading><Text color="colorPalette.fg/70" fontSize="sm">Apply to open seats and grow the list of founders you support.</Text></VStack><Button pointerEvents="none" variant="cta">Opportunities</Button></VStack></Card.Body></a></Card.Root>}</SimpleGrid>;
    $[27] = cards;
    $[28] = hasMoreFounders;
    $[29] = paths.base;
    $[30] = paths.myFoundersAll;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t10 || $[33] !== t14 || $[34] !== t15) {
    t16 = <SectionScroll>{t10}{t11}{t14}{t15}</SectionScroll>;
    $[32] = t10;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== error || $[37] !== loading || $[38] !== t16) {
    t17 = <AdvisorsQueryState error={error} loading={loading}>{t16}</AdvisorsQueryState>;
    $[36] = error;
    $[37] = loading;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};
function _temp(t0) {
  const {
    key,
    card
  } = t0;
  return <BaseballCard key={key} card={card} />;
}